import { useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import Select from 'react-select'
import { Box, Stack, Typography, Alert } from '@mui/material'
import inteApis from '@/services/IntegrationService'
import downarrowblk from '@/assets/images/chevron-down-blk.svg'
import BasicModal from '@/components/UI/Dialog/Dialog'
import {
  checkLMSCourseMapping,
  linkACourseToClass,
} from '@/store/knowledge/actions'
import {
  connectToCanvas,
  connectToOauth2Lms,
  getIntegrationCurrentCourses,
} from '@/store/quizzes/actions'

import { useTranslation } from 'react-i18next'

export const lmsListData = [
  {
    value: 'BlackboardLearn',
    filterValue: 'blackboard',
    label: 'Blackboard',
    isIntegration: true,
  },
  {
    value: 'canvas',
    filterValue: 'canvas',
    label: 'Canvas',
    isIntegration: true,
  },
  {
    value: 'desire2learn',
    filterValue: 'desire2learn',
    label: 'D2L Brightspace',
    isIntegration: false, // temporary disable D2L
  },
  {
    value: 'moodle',
    filterValue: 'moodle',
    label: 'Moodle',
    isIntegration: false, // temporary disable Moodle
  },
  {
    value: 'googleclassroom',
    filterValue: 'googleclassroom',
    label: 'Google Classroom',
  },
  {
    value: 'microsoft365',
    filterValue: 'microsoft365',
    label: 'Microsoft 365',
  },
  {
    value: 'Wizlearn',
    filterValue: 'Wizlearn',
    label: 'Wizlearn',
  },
]

const isIntegration = (provider: any) => {
  return provider.isIntegration
}

const LinkCourseToAgent = (props: any) => {
  const { t } = useTranslation('translation')
  const timerRef = useRef<NodeJS.Timeout>()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [linking, setLinking] = useState(false)
  const [checking, setChecking] = useState(false)
  const [courses, setCourses] = useState([])
  const [integrations, setIntegrations] = useState([])
  const [filteredLMSList, setFilteredLMSList] = useState([])
  const [overrideWarningMessage, setOverrideWarningMessage] = useState('')
  const [canForceOverride, setCanForceOverride] = useState(false)
  const [existingMapping, setExistingMapping] = useState<{
    class_id: string
    class_name: string
    agent_id: string
  } | null>(null)
  const [pendingLinkData, setPendingLinkData] = useState<{
    course_id: string
    provider: string
  } | null>(null)
  const [currentCourse, setCurrentCourse] = useState({
    id: '',
    label: '',
    name: '',
    value: '',
    course_code: '',
  })
  const defaultProvider = {
    value: '',
    filterValue: '',
    label: '',
  }
  const [selectedButtonTarget, setSelectedButtonTarget] = useState({
    courseId: '',
    provider: defaultProvider,
  })

  useEffect(() => {
    if (!props.show) {
      return
    }

    if (props.courseLMS) {
      const course = filteredLMSList.find(
        (d: any) => d.value === props.courseLMS.provider,
      )
      setSelectedButtonTarget({
        courseId: props.courseLMS?.course_id,
        provider: course ? course : defaultProvider,
      })
    } else {
      setSelectedButtonTarget({
        courseId: '',
        provider: defaultProvider,
      })
    }
  }, [props.show, filteredLMSList])

  const fetchIntegrations = async () => {
    const { status, data } = await inteApis.getIntegrations(null, 'api')
    if (status === 200) {
      setIntegrations(data?.data?.integrations)
    }
  }

  useEffect(() => {
    fetchIntegrations()
  }, [])

  useEffect(() => {
    if (!Array.isArray(integrations)) return

    setFilteredLMSList(
      lmsListData.map(t => {
        if (
          t.isIntegration &&
          !integrations.find(
            integration => integration.provider === t.filterValue,
          )
        ) {
          return {
            ...t,
            isIntegration: false,
          }
        }

        return t
      }),
    )
  }, [integrations])

  const linkCourseHandler = async (forceOverride: boolean = false) => {
    // If forcing override, use the pendingLinkData
    if (forceOverride && pendingLinkData) {
      const linkData = {
        ...pendingLinkData,
        force_override: true,
      }

      try {
        setLinking(true)
        await dispatch(linkACourseToClass(linkData, props.id))
        props.confirmHandler()
        resetWarningState()
        setLinking(false)
      } catch (error: any) {
        setLinking(false)
        // Note: errors are already shown by the action via toaster
      }
      return
    }

    // Normal flow: validate inputs and get courseId
    if (
      !isIntegration(selectedButtonTarget.provider) &&
      (!selectedButtonTarget.courseId || !selectedButtonTarget.provider?.value)
    ) {
      return
    }
    if (isIntegration(selectedButtonTarget.provider) && !currentCourse.id)
      return

    let courseId = ''
    if (!isIntegration(selectedButtonTarget.provider))
      courseId = selectedButtonTarget.courseId
    else if (currentCourse) {
      if (
        selectedButtonTarget.provider.label === 'Canvas' ||
        selectedButtonTarget.provider.label === 'Blackboard'
      )
        courseId = currentCourse.course_code
      else courseId = currentCourse.id
    }

    // Step 1: Check for existing mapping first
    try {
      setChecking(true)
      const existingMappingData: any = await dispatch(
        checkLMSCourseMapping({
          course_id: courseId,
          provider: selectedButtonTarget.provider.value,
        }),
      )
      setChecking(false)

      // If mapping exists, handle based on can_force_override flag
      if (existingMappingData) {
        setExistingMapping({
          class_id: existingMappingData.class_id,
          class_name: existingMappingData.class_name,
          agent_id: existingMappingData.agent_id,
        })

        if (existingMappingData.can_force_override) {
          // Can override - show inline warning
          setCanForceOverride(true)
          setPendingLinkData({
            course_id: courseId,
            provider: selectedButtonTarget.provider.value,
          })
          setOverrideWarningMessage(
            t(
              `LMS course is already mapped to class '{{className}}' in agent '{{agentName}}'. Do you want to override this mapping?`,
              {
                className: existingMappingData.class_name,
                agentName:
                  existingMappingData.agent_name ||
                  existingMappingData.agent_id,
              },
            ),
          )
        } else {
          // Cannot override - different agent owns it
          setCanForceOverride(false)
          setPendingLinkData(null)
          setOverrideWarningMessage(
            t(
              `This LMS course is already mapped to class '{{className}}' in agent '{{agentName}}'. Please remove the existing mapping in that agent first before linking it here.`,
              {
                className: existingMappingData.class_name,
                agentName:
                  existingMappingData.agent_name ||
                  existingMappingData.agent_id,
              },
            ),
          )
        }
        return
      }
      // No existing mapping, proceed to link
    } catch (error: any) {
      setChecking(false)
      // Error already shown by the action, just return
      return
    }

    // Step 2: Proceed with linking (no existing mapping found)
    const linkData = {
      course_id: courseId,
      provider: selectedButtonTarget.provider.value,
    }

    try {
      setLinking(true)
      await dispatch(linkACourseToClass(linkData, props.id))
      props.confirmHandler()
      resetWarningState()
      setLinking(false)
    } catch (error: any) {
      setLinking(false)
      // Note: errors are already shown by the action via toaster
    }
  }

  const resetWarningState = () => {
    setPendingLinkData(null)
    setExistingMapping(null)
    setCanForceOverride(false)
    setOverrideWarningMessage('')
  }

  const handleModalClose = () => {
    resetWarningState()
    props.onHide()
  }

  const inputChangeHandler = (e: any) => {
    setSelectedButtonTarget({
      ...selectedButtonTarget,
      [e.target.name]: e.target.value,
    })
  }

  const handleIntegrations = async (integration: string) => {
    connectToIntegrationHandler(integration)
  }

  const connectToIntegrationHandler = async (integration: string) => {
    try {
      setLoading(true)
      let response: any
      if (integration === 'canvas') response = await dispatch(connectToCanvas())
      else response = await dispatch(connectToOauth2Lms(integration, true))

      response.auth_url && window.open(response.auth_url, '_blank')

      if (
        response &&
        !response[`${integration}_integration`] &&
        // (integration === "canvas" && !response.canvas_integration) ||
        // (integration === "blackboard" && !response.blackboard_integration)
        !timerRef.current
      ) {
        timerRef.current = setInterval(async () => {
          let connectToTimerResponse: any
          if (integration === 'canvas') {
            connectToTimerResponse = await dispatch(connectToCanvas())
            if (connectToTimerResponse.canvas_integration) {
              getIntegrationCurrentCoursesHandler(integration)
            }
          } else {
            connectToTimerResponse = await dispatch(
              connectToOauth2Lms(integration),
            )
            if (connectToTimerResponse[`${integration}_integration`]) {
              getIntegrationCurrentCoursesHandler(integration)
            }
          }
        }, 5 * 1000)
      } else {
        getIntegrationCurrentCoursesHandler(integration)
      }
    } catch (error) {
      setLoading(false)
      console.log(error)
    }
  }

  const getIntegrationCurrentCoursesHandler = async (integration: string) => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    try {
      setLoading(true)
      const getIntegrationCurrentCoursesResponse: any = await dispatch(
        getIntegrationCurrentCourses(integration),
      )
      setCourses([
        ...getIntegrationCurrentCoursesResponse.courses.map((d: any) => {
          return { ...d, label: d.display_name, value: d.id }
        }),
      ])
      setLoading(false)
    } catch (error) {
      setLoading(false)
      // setCourses([]);
    }
  }

  useEffect(() => {
    setCourses([])
    if (isIntegration(selectedButtonTarget.provider)) {
      handleIntegrations(
        selectedButtonTarget.provider.filterValue.toLowerCase(),
      )
    }
  }, [selectedButtonTarget.provider])

  return (
    <BasicModal
      open={props.show}
      onClose={handleModalClose}
      title={t('Link Course')}
      showCloseButton
      sx={{ width: '600px' }}
      footerActions={
        existingMapping
          ? canForceOverride
            ? [
                {
                  title: t('Cancel'),
                  onClick: handleModalClose,
                  variant: 'outlined',
                  disabled: linking,
                },
                {
                  title: linking ? t('Overriding...') : t('Yes, Override'),
                  onClick: () => linkCourseHandler(true),
                  variant: 'outlined',
                  color: 'error',
                  disabled: linking,
                },
              ]
            : [
                {
                  title: t('Close'),
                  onClick: handleModalClose,
                  variant: 'contained',
                },
              ]
          : [
              {
                title: t('Cancel'),
                onClick: handleModalClose,
                variant: 'outlined',
                disabled: loading || checking || linking,
              },
              {
                title: checking
                  ? t('Checking...')
                  : linking
                    ? t('Linking...')
                    : t('Link Course'),
                onClick: () => linkCourseHandler(),
                variant: 'contained',
                disabled: checking || linking,
              },
            ]
      }
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {t('Choose LMS')}
          </Typography>
          <Select
            isSearchable={false}
            className="select-target radius-2"
            classNamePrefix="select-target-prefix"
            components={{
              DropdownIndicator: () => (
                <img src={downarrowblk} alt={t('dropdown arrow')} />
              ),
              IndicatorSeparator: () => null,
            }}
            defaultValue={selectedButtonTarget.provider || null}
            onChange={(e: any) => {
              setSelectedButtonTarget({
                ...selectedButtonTarget,
                provider: e,
              })
            }}
            placeholder={t('Choose LMS')}
            getOptionLabel={(option: any) => option['label']}
            getOptionValue={(option: any) => option['value']}
            options={filteredLMSList}
            value={selectedButtonTarget.provider || null}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 1400 }) }}
            isDisabled={!!existingMapping}
          />
        </Box>

        <Box>
          {isIntegration(selectedButtonTarget.provider) && loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Spinner animation="border" size="sm" />
              <Typography variant="body2">
                {t('Checking for login...')}
              </Typography>
            </Stack>
          )}

          {((isIntegration(selectedButtonTarget.provider) && !loading) ||
            !isIntegration(selectedButtonTarget.provider)) && (
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              {isIntegration(selectedButtonTarget.provider)
                ? t('Courses')
                : t('Course ID')}
            </Typography>
          )}

          {isIntegration(selectedButtonTarget.provider) &&
            !loading &&
            courses.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('No courses found...')}
              </Typography>
            )}

          {!isIntegration(selectedButtonTarget.provider) && (
            <input
              type="text"
              className="form-control he-35 radius-2"
              placeholder={t('Please enter course ID')}
              name="courseId"
              value={selectedButtonTarget.courseId || ''}
              onChange={inputChangeHandler}
              disabled={!!existingMapping}
            />
          )}

          {isIntegration(selectedButtonTarget.provider) &&
            courses.length > 0 && (
              <Select
                options={courses}
                isSearchable
                onChange={(e: any) => {
                  setCurrentCourse(e)
                }}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 1400 }) }}
                isDisabled={!!existingMapping}
              />
            )}
        </Box>

        {/* Inline Warning Alert - shown above action buttons */}
        {existingMapping && overrideWarningMessage && (
          <Alert severity={canForceOverride ? 'warning' : 'error'}>
            {overrideWarningMessage}
          </Alert>
        )}
      </Stack>
    </BasicModal>
  )
}

export default LinkCourseToAgent
